import { Box, Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { API_HEADERS } from '../../../../constants/apiHeaders';
import api from '../../../../services/httpClient';
import { SETTINGS_ENDPOINTS } from '../../../../services/endpoints/settings.endpoints';
import Button from '../../../common/Button';
import Dialog from '../../../common/Dialog';
import Loading from '../../../common/Loading';
import TextField from '../../../common/TextField';

interface PredefinedQuestion {
  id: number;
  question: string;
  questionLevel: 0 | 1 | 5;
  answerType: 'number' | 'currency' | 'decimal' | 'text' | 'date' | 'boolean';
  sortOrder: number;
  isActive: number;
}

interface PredefinedQuestionsModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (question: PredefinedQuestion) => void;
  filterLevel?: 0 | 1 | 5 | null;
  usedQuestionIds?: number[];
}

const PredefinedQuestionsModal: React.FC<PredefinedQuestionsModalProps> = ({
  open,
  onClose,
  onSelect,
  filterLevel,
  usedQuestionIds = [],
}) => {
  const [questions, setQuestions] = useState<PredefinedQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    if (open) {
      loadQuestions();
      setSelectedIds([]); // Reset selection when opening
      setSearchInput(''); // Reset input when opening
      setSearchQuery(''); // Reset applied filter when opening
    } else {
      // Reset search and selection when modal closes
      setSearchInput('');
      setSearchQuery('');
      setSelectedIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filterLevel]);

  const loadQuestions = async () => {
    setIsLoading(true);
    try {
      // Build query parameter if filterLevel is provided
      const queryParam = filterLevel !== null && filterLevel !== undefined 
        ? `?questionLevel=${filterLevel}` 
        : '';
      
      const response = await api.get<{ data: PredefinedQuestion[] }>(
        `${SETTINGS_ENDPOINTS.predefinedQuestions}${queryParam}`,
        {
          headers: { ...API_HEADERS.VACANCY_PREDEFINED_QUESTIONS },
        }
      );

      const loadedQuestions = response.data.data || [];
      setQuestions(loadedQuestions);
    } catch (error: any) {
      console.error('Error loading predefined questions:', error);
      setQuestions([]);
    } finally {
      setIsLoading(false);
    }
  };


  const handleSearchClick = () => {
    setSearchQuery(searchInput.trim());
  };

  const handleClearClick = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  const handleToggle = (id: number, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id)
    );
  };

  const handleAddSelected = () => {
    if (selectedIds.length === 0) {
      return; // nothing selected, do nothing
    }

    const selectedQuestions = questions.filter(
      (q) => selectedIds.includes(q.id) && !usedQuestionIds.includes(q.id)
    );
    selectedQuestions.forEach((q) => onSelect(q));
    setSelectedIds([]);
    onClose();
  };

  const filteredQuestions = questions.filter((q) => {
    // Filter by search query (level filtering is now done on backend)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return q.question.toLowerCase().includes(query);
    }

    return true;
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <Box sx={{ p: 3, background: '#FAF8FF', borderRadius: '8px' }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '24px',
            fontFamily: 'Inter',
            mb: 2,
          }}
        >
          Select Predefined Questions
        </Typography>

        <Box
          sx={{
            mb: 2,
            display: 'flex',
            gap: 1,
            alignItems: 'flex-end',
          }}
        >
          <TextField
            label="Search Questions"
            placeholder="Type to search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ width: 550 }}
          />
          <Button
            variant="contained"
            width="120px"
            onClick={handleSearchClick}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            width="120px"
            onClick={handleClearClick}
          >
            Clear
          </Button>
        </Box>

        {isLoading ? (
          <Loading message="Loading questions..." />
        ) : (
          <>
            {filteredQuestions.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', color: '#667085' }}>
                {searchQuery.trim()
                  ? 'No questions found matching your search.'
                  : 'No predefined questions available.'}
              </Box>
            ) : (
              <Box
                sx={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  border: '1px solid #E0E0E0',
                  borderRadius: '8px',
                  mb: 2,
                }}
              >
                <Table size="small">
                  <TableHead
                    sx={{
                      backgroundColor: '#F1F3FF',
                      '& th': {
                        fontWeight: 500,
                        color: '#667085',
                        borderBottom: '1px solid #EAECF0',
                        padding: '10px 12px',
                        fontSize: '13px !important',
                        verticalAlign: 'middle',
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell>Question</TableCell>
                      <TableCell sx={{ width: '140px' }}>Answer Type</TableCell>
                      <TableCell sx={{ width: '80px', textAlign: 'center' }}>Select</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredQuestions.map((question) => {
                      const isUsed = usedQuestionIds.includes(question.id);

                      return (
                        <TableRow
                          key={question.id}
                          hover
                          sx={{
                            '&:hover': {
                              backgroundColor: '#F9FAFB',
                            },
                            '& td': {
                              padding: '10px 12px',
                              color: '#000000',
                              fontSize: '12px',
                              fontWeight: 500,
                              verticalAlign: 'middle',
                              borderBottom: '1px solid #F0F0F0',
                            },
                          }}
                        >
                          <TableCell sx={{ minWidth: 0 }}>
                            {question.question}
                            {isUsed && (
                              <Typography
                                component="span"
                                sx={{ ml: 1, fontSize: '11px', color: '#667085', fontStyle: 'italic' }}
                              >
                                (Already added)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ textTransform: 'capitalize', width: '140px' }}>
                            {question.answerType}
                          </TableCell>
                          <TableCell sx={{ width: '80px', textAlign: 'center', padding: '4px 12px' }}>
                            <Checkbox
                              checked={isUsed || selectedIds.includes(question.id)}
                              disabled={isUsed}
                              onChange={(e) => {
                                if (!isUsed) {
                                  handleToggle(question.id, e.target.checked);
                                }
                              }}
                              sx={{ padding: 0 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="outlined" width="120px" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="contained"
            width="160px"
            onClick={handleAddSelected}
          >
            Add Selected ({selectedIds.length})
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default PredefinedQuestionsModal;

